import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';

const isElementInViewport = (element: HTMLElement) => {
  return element.getBoundingClientRect().y < window?.innerHeight;
};

const hasScrolledToBottom = () => {
  return window.innerHeight + window.scrollY >= document.body.offsetHeight;
};

export const useContentGatePopup = ({
  isEnabled,
  useIntersectionLogic,
  contentGateElementRef,
}: {
  isEnabled: boolean;
  useIntersectionLogic: boolean;
  contentGateElementRef?: MutableRefObject<HTMLDivElement | null>;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const { currentModal, handleOpenModal, handleCloseModal } = useGlobalModals();

  const updateIntersection = useCallback(() => {
    if (!useIntersectionLogic) {
      if (hasScrolledToBottom()) {
        setIsIntersecting(true);
      } else {
        setIsIntersecting(false);
      }
      return;
    }

    if (!contentGateElementRef?.current) {
      return;
    }

    if (isElementInViewport(contentGateElementRef.current)) {
      setIsIntersecting(true);
    } else {
      setIsIntersecting(false);
    }
  }, [contentGateElementRef, useIntersectionLogic]);

  // Observe when the content gate element scrolls into viewport
  // and update state
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    // Show element if it's already in viewport when the page loads
    if (useIntersectionLogic && contentGateElementRef?.current && isElementInViewport(contentGateElementRef.current)) {
      setIsIntersecting(true);
      return;
    }

    // Show element when user scrolls to the content gate
    window.addEventListener('scroll', updateIntersection);
    return () => {
      window.removeEventListener('scroll', updateIntersection);
    };
  }, [contentGateElementRef?.current, updateIntersection, useIntersectionLogic, isEnabled]);

  // Open popup
  useEffect(() => {
    if (isEnabled && isIntersecting && !isOpen && currentModal !== 'content_gate') {
      handleOpenModal('content_gate', { preventScrollReset: true });
      setIsOpen(true);
    }
  }, [currentModal, handleOpenModal, isOpen, isIntersecting, isEnabled]);

  // Remove search param if user lands on the page with search param modal=email_capture
  // but it's not anymore applicable and the modal shouldn't be shown
  // or the gate is not in viewport anymore
  useEffect(() => {
    if (currentModal === 'content_gate' && (!isOpen || (isOpen && !isIntersecting))) {
      handleCloseModal({ preventScrollReset: true });
      setIsOpen(false);
    }
  }, [currentModal, isOpen, isIntersecting, handleCloseModal]);

  return {
    isOpen: isOpen && currentModal === 'content_gate',
  };
};
